import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api, formatApiErrorDetail } from "../lib/apiClient";

export default function LoginPage() {
  const { login, ipBlocked: authIpBlocked } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [pageCfg, setPageCfg] = useState(null);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  // If the backend IP allow-list blocks us, show a hard "access denied" screen.
  const [ipBlocked, setIpBlocked] = useState(authIpBlocked || null);

  useEffect(() => {
    // Defensive: nuke any stale `access_token` cookie variants that older
    // deployments may have left behind (those caused CF-520 errors on login).
    api.post("/auth/clear-cookies").catch(() => {});
    // public branches list for the registration dropdown
    api.get("/branches/public").then((r) => setBranches(r.data)).catch((ex) => {
      // 403 here means the IP allow-list is blocking this device
      if (ex?.response?.status === 403) {
        setIpBlocked(ex.response.data?.detail || "Access denied for your IP address.");
        return;
      }
      // fallback to hard-coded if endpoint not available
      setBranches([
        { name: "Plymouth" },
        { name: "Bristol" },
      ]);
    });
    api.get("/login-page").then((r) => setPageCfg(r.data)).catch(() => setPageCfg(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setInfo("");
    try {
      if (mode === "login") {
        await login(email, password);
        const target = loc.state?.from || "/";
        nav(target, { replace: true });
      } else {
        // Register — DO NOT auto-login (account pending)
        await api.post("/auth/register", {
          email, password,
          name: name || email.split("@")[0],
          branch,
        });
        setInfo(
          "Account created. An administrator must approve your account before you can sign in. You'll be notified once it's been approved."
        );
        setMode("login");
        setPassword("");
      }
    } catch (ex) {
      if (ex?.response?.status === 403) {
        setIpBlocked(ex.response.data?.detail || "Access denied for your IP address.");
        return;
      }
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  if (ipBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-6" data-testid="ip-blocked-screen">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-600 font-bold mb-2">Access denied</div>
          <h1 className="font-display text-2xl font-black tracking-tight mb-3">This device isn't authorised</h1>
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="ip-blocked-detail">{ipBlocked}</p>
          <p className="text-[11px] text-slate-400 mt-6 font-mono">Quote Matrix · IP allow-list active</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1776316170808-f59232c1a85e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwcm9sbGVyJTIwZG9vcnN8ZW58MHx8fHwxNzc5MTM2MDA4fDA&ixlib=rb-4.1.0&q=85"
          alt="Industrial warehouse"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 inline-flex rounded-lg">
              <img
                src={pageCfg?.has_custom_logo ? `${process.env.REACT_APP_BACKEND_URL}/api/login-page/logo?ts=${pageCfg?.updated_at || ""}` : "/brand/entry-group-logo.jpeg"}
                alt="Logo"
                className="h-12 w-auto rounded"
                onError={(e) => { e.currentTarget.src = "/brand/entry-group-logo.jpeg"; }}
              />
            </div>
            <div className="font-display font-black tracking-tighter text-xl leading-tight">
              <div>Quote Matrix</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-bold mt-0.5">By Entry Group</div>
            </div>
          </div>
          <div className="space-y-6 max-w-md">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold inline-block px-2 py-1 bg-brand-orange text-white rounded">
              Industrial Quoting · v1.0
            </div>
            <h1 className="font-display text-5xl leading-none font-black tracking-tighter" data-testid="login-headline">
              {pageCfg?.headline || "Stop fighting the spreadsheet."}
            </h1>
            <p className="text-slate-200/90 text-base leading-relaxed whitespace-pre-line" data-testid="login-subtitle">
              {pageCfg?.subtitle || "Replace your Quote Matrix XLSM with a live, multi-user web app — same logic, faster output, fewer formula errors."}
            </p>
            {pageCfg?.welcome_text && (
              <p className="text-slate-200/80 text-sm leading-relaxed whitespace-pre-line border-l-2 border-brand-orange pl-4" data-testid="login-welcome">
                {pageCfg.welcome_text}
              </p>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-300" data-testid="login-footer">
            {pageCfg?.footer_text || "© Entry Group · Built for trade professionals"}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 px-6 py-12">
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8 fade-up"
          data-testid="auth-form"
        >
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">
              {mode === "login" ? "Sign in" : "Create account"}
            </div>
            <h2 className="font-display text-3xl font-black tracking-tighter">
              {mode === "login" ? "Welcome back." : "Get started."}
            </h2>
            {mode === "register" && (
              <p className="text-xs text-slate-500 mt-2">
                Your account will be reviewed and approved by an administrator before you can sign in.
              </p>
            )}
          </div>

          {mode === "register" && (
            <>
              <div className="mb-4">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
                  Full name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="qb-input"
                  data-testid="register-name-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
                  Branch / Depot *
                </label>
                <select
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="qb-input"
                  data-testid="register-branch-select"
                  required
                >
                  <option value="">— Select your branch —</option>
                  {branches.map((b) => <option key={b.name}>{b.name}</option>)}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  You will only see quotes that belong to your branch.
                </p>
              </div>
            </>
          )}
          <div className="mb-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="qb-input font-mono text-sm"
              data-testid="email-input"
            />
          </div>
          <div className="mb-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="qb-input font-mono text-sm"
              data-testid="password-input"
            />
          </div>

          {info && (
            <div className="border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-3 py-2 mb-4 rounded-lg" data-testid="auth-info">
              {info}
            </div>
          )}
          {err && (
            <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 mb-4 rounded-lg" data-testid="auth-error">
              {err}
            </div>
          )}

          <button
            disabled={busy}
            type="submit"
            className="w-full h-11 bg-brand-blue text-white font-semibold uppercase tracking-wider text-sm hover:bg-brand-blue-dark disabled:opacity-60 rounded-full transition-colors"
            data-testid="auth-submit-button"
          >
            {busy ? "Working…" : mode === "login" ? "Sign in" : "Request access"}
          </button>

          <button
            type="button"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); setInfo(""); }}
            className="mt-4 text-xs uppercase tracking-wider text-slate-500 hover:text-brand-blue w-full font-semibold"
            data-testid="toggle-mode-button"
          >
            {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
          </button>

          {mode === "login" && (
            <button
              type="button"
              onClick={() => nav("/forgot-password")}
              className="mt-2 text-xs uppercase tracking-wider text-slate-500 hover:text-brand-blue w-full font-semibold"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
